import React, { useState } from "react";
import { OverCreditPermissionProps } from "./OverCreditPermission";
import CommonButtons from "src/shared/components/CommonButtons";

export default function UserBased({ approvalContacts, onConfirm, onCancel, loading = false }: OverCreditPermissionProps) {
  const [selectedIndexes, setSelectedIndexes] = useState<Set<number>>(new Set());

  const allSelected = approvalContacts.length > 0 && selectedIndexes.size === approvalContacts.length;
  const someSelected = selectedIndexes.size > 0 && !allSelected;

  const toggleContact = (index: number) => {
    if (loading) return;
    setSelectedIndexes((prev) => {
      const next = new Set(prev);
      if (next.has(index)) {
        next.delete(index);
      } else {
        next.add(index);
      }
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (loading) return;
    if (allSelected) {
      setSelectedIndexes(new Set());
    } else {
      setSelectedIndexes(new Set(approvalContacts.map((_, i) => i)));
    }
  };

  const handleConfirm = () => {
    if (loading || selectedIndexes.size === 0) return;
    const selectedContacts = approvalContacts.filter((_, i) => selectedIndexes.has(i));
    onConfirm({ type: "contacts", selectedContacts });
  };

  return (
    <div>
      <div className="flex items-start gap-3 mb-4">
        <div className="flex-shrink-0 w-10 h-10 rounded-full bg-amber-50 flex items-center justify-center">
          <svg className="w-5 h-5 text-amber-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" />
          </svg>
        </div>
        <div>
          <h3 className="text-base font-semibold text-gray-900">Over Credit Limit</h3>
          <p className="text-sm text-gray-500 mt-0.5">The customer has exceeded their credit limit. Please review the order and adjust the payment or credit limit before proceeding.</p>
        </div>
      </div>

      {/* Approval contacts */}
      <div className="mt-5">
        <div className="flex items-center justify-between mb-2">
          <h4 className="text-sm font-medium text-gray-700">Approval Contacts</h4>
          <label className="flex items-center gap-1.5 text-xs font-medium text-gray-500 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={allSelected}
              ref={(el) => {
                if (el) el.indeterminate = someSelected;
              }}
              onChange={toggleSelectAll}
              disabled={loading}
              className="w-3.5 h-3.5 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
            />
            Select all
          </label>
        </div>

        <ul className="border border-gray-200 rounded-lg divide-y divide-gray-100 overflow-hidden">
          {approvalContacts.map((contact, index) => {
            const checked = selectedIndexes.has(index);
            return (
              <li
                key={index}
                onClick={() => toggleContact(index)}
                className={`flex items-center gap-3 px-3 py-2.5 cursor-pointer transition-colors ${
                  checked ? "bg-indigo-50" : "bg-white hover:bg-gray-50"
                } ${loading ? "opacity-60 pointer-events-none" : ""}`}
              >
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={() => toggleContact(index)}
                  onClick={(e) => e.stopPropagation()}
                  disabled={loading}
                  className="w-4 h-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 truncate">{contact.name}</p>
                  <p className="text-xs text-gray-500 truncate">{contact.email}</p>
                </div>
                <span className="text-xs font-medium text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full flex-shrink-0">{contact.role}</span>
              </li>
            );
          })}
        </ul>

        {approvalContacts.length === 0 && <p className="text-sm text-gray-400 text-center py-4">No approval contacts available.</p>}
      </div>

      {/* Actions */}
      <div className="flex justify-end gap-2 mt-6">
        <CommonButtons label="Cancel" variant="outlineDanger" onClick={onCancel} disabled={loading} />
        <CommonButtons
          label={loading ? "Sending..." : `Confirm${selectedIndexes.size > 0 ? ` (${selectedIndexes.size})` : ""}`}
          onClick={handleConfirm}
          disabled={selectedIndexes.size === 0 || loading}
        />
      </div>
    </div>
  );
}
